import {
    ChangeDetectionStrategy,
    Component,
    computed,
    contentChild,
    effect,
    ElementRef,
    inject,
    input,
    output,
    TemplateRef,
    viewChild,
    ViewContainerRef
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { CdkConnectedOverlay, ConnectedPosition, Overlay, OverlayModule, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { MatButtonModule } from '@angular/material/button';
import { TranslateModule } from '@ngx-translate/core';
import { OverlayDialogFormContent } from './overlay-dialog-form';
import { OverlayDialogService } from './overlay-dialog.service';
import { IconComponent } from '../icon/icon.component';

@Component({
    selector: 'ca-overlay-dialog',
    standalone: true,
    imports: [OverlayModule, MatButtonModule, IconComponent, NgTemplateOutlet, TranslateModule],
    templateUrl: './overlay-dialog.component.html',
    styleUrls: ['./overlay-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class OverlayDialogComponent {
    private readonly overlay = inject(Overlay);
    private readonly viewContainerRef = inject(ViewContainerRef);
    private readonly overlayDialogService = inject(OverlayDialogService);
    public readonly anchorElement = input.required<ElementRef<HTMLElement> | HTMLElement>();
    public readonly confirmLabel = input.required<string>();
    public readonly fullScreen = input<boolean>(false);
    public readonly width = input<number>(520);
    public readonly open = input<boolean>(true);
    public readonly confirmed = output<unknown>();
    public readonly closed = output<void>();
    public readonly canceled = output<void>();

    protected readonly contentElement = contentChild(OverlayDialogFormContent);
    private readonly surfaceElement = viewChild<TemplateRef<unknown>>('surfaceElement');
    private readonly connectedOverlay = viewChild(CdkConnectedOverlay);

    protected readonly isMobile = this.overlayDialogService.isMobile;

    protected readonly positions = computed<ConnectedPosition[]>(() =>
        this.isMobile()
            ? [{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'top', offsetY: 130 }]
            : [{ originX: 'center', originY: 'center', overlayX: 'center', overlayY: 'center' }]
    );

    private readonly repositionOnAnchorResize = effect(onCleanup => {
        if (this.fullScreen() || !this.open()) {
            return;
        }

        const anchor = this.anchorElement();
        const element = anchor instanceof ElementRef ? anchor.nativeElement : anchor;
        const observer = new ResizeObserver(() => this.connectedOverlay()?.overlayRef?.updatePosition());

        observer.observe(element);
        onCleanup(() => observer.disconnect());
    });

    private fullScreenOverlayRef: OverlayRef | null = null;

    private readonly syncFullScreenOverlay = effect(() => {
        if (!this.fullScreen()) {
            return;
        }

        const surface = this.surfaceElement();

        if (this.open() && surface) {
            this.openFullScreen(surface);
        } else {
            this.fullScreenOverlayRef?.detach();
        }
    });

    private readonly disposeFullScreenOverlayOnDestroy = effect(onCleanup => {
        onCleanup(() => this.fullScreenOverlayRef?.dispose());
    });

    protected confirm(): void {
        const form = this.contentElement()?.form;

        if (form?.invalid) {
            form.markAllAsTouched();
            return;
        }

        this.closed.emit();
        this.confirmed.emit(form?.getRawValue());
    }
    protected cancel(): void {
        this.closed.emit();
        this.canceled.emit();
    }

    private openFullScreen(surface: TemplateRef<unknown>): void {
        if (this.fullScreenOverlayRef?.hasAttached()) {
            return;
        }

        this.fullScreenOverlayRef ??= this.overlay.create({
            positionStrategy: this.overlay.position().global(),
            scrollStrategy: this.overlay.scrollStrategies.block(),
            width: '100vw',
            height: '100vh',
            hasBackdrop: false
        });

        this.fullScreenOverlayRef.attach(new TemplatePortal(surface, this.viewContainerRef));
    }
}
